/**
 * NetDocuments Token Manager
 *
 * Centralised, non-React token management for the ND OAuth integration.
 * Used by both HTTP interceptors (client.ts / apiInstance.ts) and React
 * hooks (useNetDocuments.ts, SidebarAuthButtons.tsx).
 *
 * Key guarantees:
 *  - `expires_on` is always written when a token is stored.
 *  - `isNdTokenExpired` treats a missing `expires_on` as expired (safe default).
 *  - `getValidNdAccessToken` checks expiry and refreshes before returning.
 *  - Concurrent refresh calls are deduplicated via a shared promise.
 */

import axios from 'axios';
import { apiConfig } from '@/config';

// ── Storage keys ────────────────────────────────────────────────────────────
export const ND_TOKEN_KEY = 'NDToken';
export const ND_AUTHENTICATED_KEY = 'NDAuthenticated';
export const ND_AUTH_CHANGED_EVENT = 'nd-auth-changed';

// ── Types ───────────────────────────────────────────────────────────────────
export interface NDTokenData {
  access_token: string;
  refresh_token: string;
  authorization_code?: string;
  expires_in: number;
  /** ISO string – always set by storeNdToken */
  expires_on?: string;
}

// ── Internal state ──────────────────────────────────────────────────────────
let refreshPromise: Promise<boolean> | null = null;

// Bearer token getter – set by AuthProvider (avoids circular dep with client.ts)
let getBearerToken: () => Promise<string | null> = async () => null;

/** Called by AuthProvider to wire up the app Bearer token for ND refresh calls. */
export const setNdBearerTokenGetter = (getter: () => Promise<string | null>): void => {
  getBearerToken = getter;
};

// ── Read / write helpers ────────────────────────────────────────────────────

export const getStoredNdToken = (): NDTokenData | null => {
  try {
    const raw = localStorage.getItem(ND_TOKEN_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as NDTokenData;
  } catch {
    return null;
  }
};

export const storeNdToken = (token: NDTokenData): void => {
  const expiresIn = token.expires_in || 3600;
  const tokenWithExpiry: NDTokenData = {
    ...token,
    expires_on: new Date(Date.now() + expiresIn * 1000).toISOString(),
  };
  localStorage.setItem(ND_TOKEN_KEY, JSON.stringify(tokenWithExpiry));
  localStorage.setItem(ND_AUTHENTICATED_KEY, 'true');

  window.dispatchEvent(
    new CustomEvent(ND_AUTH_CHANGED_EVENT, { detail: { authenticated: true } }),
  );
};

export const clearNdToken = (): void => {
  localStorage.removeItem(ND_TOKEN_KEY);
  localStorage.removeItem(ND_AUTHENTICATED_KEY);

  window.dispatchEvent(
    new CustomEvent(ND_AUTH_CHANGED_EVENT, { detail: { authenticated: false } }),
  );
};

// ── Expiry check ────────────────────────────────────────────────────────────

/** 5-minute buffer so we refresh before the token actually expires. */
const EXPIRY_BUFFER_MS = 5 * 60 * 1000;

export const isNdTokenExpired = (token: NDTokenData): boolean => {
  // No expiry info → treat as expired (safe default)
  if (!token.expires_on) return true;
  const expiresAt = new Date(token.expires_on).getTime();
  return Date.now() > expiresAt - EXPIRY_BUFFER_MS;
};

// ── Refresh ─────────────────────────────────────────────────────────────────

/**
 * Refresh the ND access token using the stored refresh_token.
 * Concurrent calls are deduplicated – only one network request fires.
 */
export const refreshNdToken = async (refreshTokenValue: string): Promise<boolean> => {
  if (refreshPromise) return refreshPromise;

  refreshPromise = (async () => {
    try {
      // The /NDApi/GetAccessToken endpoint requires ABP authentication ([Authorize]).
      // Attach the Bearer token so the request isn't rejected with 401.
      const headers: Record<string, string> = {};
      const bearer = await getBearerToken();
      if (bearer) {
        headers['Authorization'] = `Bearer ${bearer}`;
      }

      // Resolve tenant for the __tenant header (same as apiClient interceptor)
      try {
        const { resolveTenant } = await import('@/tenant/tenantResolver');
        const tenant = resolveTenant();
        if (tenant) headers['__tenant'] = tenant;
      } catch { /* tenantResolver not available yet */ }

      const response = await axios.get<NDTokenData>(
        `${apiConfig.baseUrl}/NDApi/GetAccessToken`,
        { params: { tokenCode: refreshTokenValue }, headers },
      );

      if (response.data?.access_token) {
        // Preserve the existing refresh_token if the server didn't return a new one.
        // NetDocuments refresh-grant responses may omit the refresh_token field,
        // which would overwrite the stored value with null and break subsequent refreshes.
        const tokenToStore: NDTokenData = {
          ...response.data,
          refresh_token: response.data.refresh_token || refreshTokenValue,
        };
        storeNdToken(tokenToStore);
        return true;
      }
    } catch (err) {
      console.error('Failed to refresh ND token:', err);
      // NEVER clear the token here. The failure could be because:
      // - The app Bearer token isn't ready yet (AuthProvider hasn't mounted)
      // - Transient network error
      // - Server temporarily unavailable
      // The token stays in localStorage so a subsequent attempt can succeed.
    }
    return false;
  })();

  try {
    return await refreshPromise;
  } finally {
    refreshPromise = null;
  }
};

// ── High-level accessor ─────────────────────────────────────────────────────

/**
 * Return a valid ND access_token string, refreshing first if needed.
 * Returns `null` when no token is stored or refresh fails.
 */
export const getValidNdAccessToken = async (): Promise<string | null> => {
  const token = getStoredNdToken();
  if (!token?.access_token) return null;

  if (isNdTokenExpired(token)) {
    if (!token.refresh_token) {
      // No refresh token available — can't recover, but don't clear the stored
      // token; the user may reconnect via the OAuth popup.
      return null;
    }
    const ok = await refreshNdToken(token.refresh_token);
    if (!ok) return null;
    const refreshed = getStoredNdToken();
    return refreshed?.access_token || null;
  }

  return token.access_token;
};
